import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';

import { zodResolver } from '@hookform/resolvers/zod';
import { CircularProgress } from '@mui/material';
import { useContentManagement } from 'api/portal/useContentManagement';
import useLocalization from 'api/useLocalization';
import { PageContentData } from 'common/portalContracts';
import { sanitizeSlug } from 'utils/portalUtils';
import { EToasterStatus, showToast } from 'utils/showToast';
import {
  calculateAllLanguageStatuses,
  calculateLanguageStatus,
  createIncompleteTranslationsModalContent,
  getEnabledUniqueLanguages,
  getIncompleteLanguages,
  getInitialFormValues,
  handleLanguageSwitch,
  isDefaultLanguage,
  prepareContentSubmission,
  saveDraftToRef,
  transformLanguageValue,
  validateAllLanguagesComplete
} from 'views/Content/ContentForm/utils';
import * as z from 'zod';

import FormHeader from 'components/FormHeader/FormHeader';
import Input from 'components/Input/Input';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import {
  DEFAULT_LANGUAGE,
  LANGUAGE_STATUS
} from 'components/LanguagePicker/constants';
import { LanguagePicker } from 'components/LanguagePicker/LanguagePicker';
import RichText from 'components/RichText/RichText';
import { MarkdownEditorWrapper } from 'components/RichTextEditor/MarkdownEditorWrapper';
import { TagSelector } from 'components/TagSelector/TagSelector';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage
} from 'components/ui';
import { Separator } from 'components/ui/separator';
import UploadImage from 'components/UploadImage/UploadImage';

import DatePicker from '../../../components/DatePicker/DatePicker';
import { PORTAL_MODAL_NAMES } from '../../../components/DialogModalManager/constants/modalConfigs';
import Switch from '../../../components/Switch/Switch';
import { closeModal, openModal } from '../../../store/portalModalDialogSlice';

import {
  CONTENT_FORM_SCHEMA,
  CONTENT_RADIO_BUTTON_OPTIONS,
  CONTENT_TYPE_OPTIONS,
  EContentRadioButtonOptions,
  EContentTypeOptions,
  EVisibilityOptions,
  TRANSLATABLE_FIELDS,
  VISIBILITY_OPTIONS
} from './constants';
import { PagesSelect } from './PageSelect';
import { LanguageStatus, TranslationDraftsMap } from './types';

type ContentFormProps = {
  dup?: boolean;
};

const ContentForm: FC<ContentFormProps> = ({ dup = false }) => {
  const {
    createPageContent,
    getCreatedContentsData,
    getPageContentById,
    editPageContent
  } = useContentManagement();
  const { getAddedLanguagesList } = useLocalization();
  const { contentId } = useParams<{ contentId: string }>();
  const currentContentsData = getPageContentById?.data;
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const [languageStatus, setLanguageStatus] = useState<LanguageStatus>({});
  const [interactedFields, setInteractedFields] = useState<Set<string>>(
    new Set()
  );

  const translationDraftsRef = useRef<TranslationDraftsMap>({});

  const isLanguageDataLoading = getAddedLanguagesList.isLoading;

  const enabledLanguages = useMemo(() => {
    return getEnabledUniqueLanguages(getAddedLanguagesList?.data);
  }, [getAddedLanguagesList?.data]);

  const initialValues = useMemo(() => {
    return getInitialFormValues({
      currentContentsData,
      dup,
      EContentRadioButtonOptions,
      EContentTypeOptions,
      EVisibilityOptions,
      DEFAULT_LANGUAGE
    });
  }, [currentContentsData, dup]);

  const contentFormConfig = useForm<z.infer<typeof CONTENT_FORM_SCHEMA>>({
    resolver: zodResolver(CONTENT_FORM_SCHEMA),
    defaultValues: initialValues,
    mode: 'onChange'
  });
  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    getValues,
    formState: { errors, isValid, isDirty }
  } = contentFormConfig;

  const titleValue = watch('title');
  const localizationValue = watch('localization');
  const labelsValue = watch('labels');

  const labelsDisplayValue = useMemo(
    () =>
      labelsValue && Array.isArray(labelsValue) && labelsValue.length > 0
        ? labelsValue.join(', ')
        : '',
    [labelsValue]
  );

  const getCurrentLanguage = () =>
    localizationValue?.value || DEFAULT_LANGUAGE.value;

  const markAsInteracted = (fieldName: string) => {
    setInteractedFields((prev) => new Set(prev).add(fieldName));
  };

  const handleLanguageChange = useCallback(
    (newLanguageValue: string, onChange: (value: any) => void) => {
      const currentLanguage = getCurrentLanguage();
      const formData = getValues();

      const mergedData = handleLanguageSwitch({
        currentLanguage,
        newLanguage: newLanguageValue,
        draftsRef: translationDraftsRef,
        currentFormData: formData,
        currentContentsData,
        initialValues
      });

      const languageObject = transformLanguageValue(
        newLanguageValue,
        enabledLanguages
      );
      mergedData.localization = languageObject;

      setInteractedFields(new Set());

      reset(mergedData);
    },
    [localizationValue, currentContentsData, initialValues, enabledLanguages]
  );

  useEffect(() => {
    if (titleValue && !contentId && !dup) {
      const formattedTitle = sanitizeSlug(titleValue);
      setValue('slug', formattedTitle);
    }
  }, [titleValue, setValue, contentId, dup]);

  useEffect(() => {
    if (currentContentsData) {
      reset(initialValues);
    }
  }, [currentContentsData, reset, initialValues]);

  useEffect(() => {
    const currentLanguage = getCurrentLanguage();
    const formData = getValues();
    const initialStatus = calculateAllLanguageStatuses(
      currentLanguage,
      formData,
      translationDraftsRef,
      currentContentsData?.translations
    );
    setLanguageStatus(initialStatus);
  }, [currentContentsData?.translations, localizationValue]);

  useEffect(() => {
    let timeoutId: any;

    const subscription = watch((data, { name, type }) => {
      if (
        type === 'change' &&
        name &&
        TRANSLATABLE_FIELDS.includes(name as any)
      ) {
        const currentLanguage = getCurrentLanguage();
        const currentFormData = getValues();
        const currentStatus =
          languageStatus[currentLanguage] || LANGUAGE_STATUS.EMPTY;

        clearTimeout(timeoutId);

        timeoutId = setTimeout(() => {
          const newStatus = calculateLanguageStatus(currentFormData as any);

          if (newStatus !== currentStatus) {
            setLanguageStatus((prev) => ({
              ...prev,
              [currentLanguage]: newStatus
            }));
          }
        }, 500);
      }
    });

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeoutId);
    };
  }, [localizationValue, languageStatus]);

  const isUpdateButtonEnabled = useMemo(() => {
    const currentLanguage = getCurrentLanguage();
    const englishIsConfigured =
      languageStatus[DEFAULT_LANGUAGE.value] === LANGUAGE_STATUS.COMPLETE;
    const isTranslationMode =
      currentLanguage !== DEFAULT_LANGUAGE.value && englishIsConfigured;
    if (isTranslationMode) return true;
    if (!currentContentsData || dup) return isValid;

    const hasAnyDrafts = Object.keys(translationDraftsRef.current).length > 0;
    return isValid && (isDirty || hasAnyDrafts);
  }, [
    currentContentsData,
    dup,
    isValid,
    isDirty,
    languageStatus,
    localizationValue
  ]);

  const BUTTON_TEXT = currentContentsData && !dup ? 'Update' : 'Save';

  const onCloseForm = () => {
    navigate('../');
  };

  const handleCreateContent = async (data: PageContentData) => {
    try {
      await createPageContent.mutateAsync(data);
      await getCreatedContentsData.refetch();

      translationDraftsRef.current = {};

      showToast({
        message: 'New content was created successfully',
        status: EToasterStatus.SUCCESS
      });
      onCloseForm();
    } catch (error: any) {
      showToast({
        message:
          error?.response?.data?.message ||
          'New content was not created. Please try again',
        status: EToasterStatus.ERROR
      });
    }
  };

  const handleEditSelectedContent = async (data: PageContentData) => {
    try {
      await editPageContent.mutateAsync(data);
      await getCreatedContentsData.refetch();

      translationDraftsRef.current = {};

      showToast({
        message: 'Page content was successfully updated',
        status: EToasterStatus.SUCCESS
      });
      navigate('../');
    } catch (error: any) {
      showToast({
        message:
          error?.response?.data?.message ||
          'Page content was not updated. Please try again',
        status: EToasterStatus.ERROR
      });
    }
  };

  const executeSubmission = useCallback(
    (data: z.infer<typeof CONTENT_FORM_SCHEMA>) => {
      const currentLanguage = getCurrentLanguage();

      const isEditing = currentContentsData && !dup;
      const submissionData = prepareContentSubmission(
        currentLanguage,
        data,
        translationDraftsRef,
        languageStatus,
        isEditing
      );
      const { publishDate } = submissionData;

      const baseData =
        currentLanguage === DEFAULT_LANGUAGE.value
          ? data
          : {
              ...initialValues,
              ...((currentContentsData &&
                Object.fromEntries(
                  Object.entries(currentContentsData).filter(
                    ([key]) =>
                      ![
                        'id',
                        'createdAt',
                        'updatedAt',
                        'version',
                        '_id'
                      ].includes(key)
                  )
                )) ||
                {}),
              ...translationDraftsRef.current[DEFAULT_LANGUAGE.value]
            };

      const {
        localization,
        isPublishDateEnabled,
        publishDate: formPublishDate,
        ...cleanBaseData
      } = baseData;

      const shouldUpdatePublishDate =
        publishDate &&
        publishDate.trim() &&
        currentLanguage !== DEFAULT_LANGUAGE.value &&
        submissionData.translations.length > 0;

      const finalSubmissionData = {
        ...cleanBaseData,
        state: submissionData.state,
        translations: submissionData.translations,
        pages: data.pages,
        slug: data.slug,
        ...(data.tag && { tag: data.tag }),
        ...(publishDate &&
          publishDate.trim() && {
            publishDate: shouldUpdatePublishDate
              ? new Date().toISOString()
              : submissionData.publishDate
          })
      };

      if (currentContentsData && !dup) {
        handleEditSelectedContent(finalSubmissionData);
      } else {
        handleCreateContent(finalSubmissionData);
      }
    },
    [languageStatus, currentContentsData, dup, initialValues]
  );

  const handleIncompleteTranslations = useCallback(
    (incompleteLanguages: string[], formData: any) => {
      const descriptionContent =
        createIncompleteTranslationsModalContent(incompleteLanguages);

      dispatch(
        openModal({
          currentModal: PORTAL_MODAL_NAMES.INCOMPLETE_TRANSLATIONS,
          customDescriptionContent: descriptionContent,
          modalActions: {
            onConfirm: {
              label: 'Save Anyway',
              action: () => {
                dispatch(closeModal());
                executeSubmission(formData);
              }
            },
            onClose: {
              label: 'Cancel',
              action: () => dispatch(closeModal()),
              variant: 'link'
            }
          }
        })
      );
    },
    [dispatch, executeSubmission]
  );

  const handleContentFormSubmit = useCallback(
    (data: z.infer<typeof CONTENT_FORM_SCHEMA>) => {
      const currentLanguage = getCurrentLanguage();
      saveDraftToRef(translationDraftsRef, currentLanguage, data);

      if (!validateAllLanguagesComplete(languageStatus)) {
        const incompleteLanguages = getIncompleteLanguages(
          languageStatus,
          enabledLanguages
        );
        handleIncompleteTranslations(incompleteLanguages, data);
        return;
      }

      executeSubmission(data);
    },
    [languageStatus, enabledLanguages]
  );

  const handleFormSubmission = useCallback(() => {
    const currentLanguage = getCurrentLanguage();
    const englishIsConfigured =
      languageStatus[DEFAULT_LANGUAGE.value] === LANGUAGE_STATUS.COMPLETE;

    if (currentLanguage !== DEFAULT_LANGUAGE.value && englishIsConfigured) {
      const formData = getValues();
      handleContentFormSubmit(formData);
    } else {
      handleSubmit(handleContentFormSubmit)();
    }
  }, [languageStatus]);

  const handleOptionChange = (option: EContentRadioButtonOptions) => {
    dispatch(
      openModal({
        currentModal: PORTAL_MODAL_NAMES.PUBLISH_CONTENT,
        modalActions: {
          onConfirm: {
            label: 'Save changes',
            action: () => {
              setValue('state', option, {
                shouldDirty: true,
                shouldValidate: true
              });
              if (option === EContentRadioButtonOptions.HIDDEN) {
                setValue('publishDate', '', {
                  shouldDirty: true,
                  shouldValidate: true
                });
              }

              if (option === EContentRadioButtonOptions.VISIBLE) {
                setValue('publishDate', '', {
                  shouldDirty: true,
                  shouldValidate: true
                });
                setValue('isPublishDateEnabled', false, {
                  shouldDirty: true,
                  shouldValidate: true
                });
              }
              dispatch(closeModal());
            }
          },
          onClose: {
            label: 'Not Now',
            action: () => {
              dispatch(closeModal());
            },
            variant: 'link'
          }
        }
      })
    );
  };

  return (
    <div className="bg-white fixed h-screen w-full top-0 left-0 z-[1000] overflow-scroll">
      {((dup || !!contentId) && getPageContentById.isLoading) ||
      isLanguageDataLoading ? (
        <div className="absolute w-full h-full top-0 left-0 z-50 flex items-center justify-center bg-white/60 backdrop-blur-sm">
          <CircularProgress />
        </div>
      ) : null}
      <FormHeader
        closeFormOnSubmit={false}
        onCloseForm={onCloseForm}
        onMainButtonClick={handleFormSubmission}
        onOptionChange={(option) => {
          handleOptionChange(option as EContentRadioButtonOptions);
        }}
        selectedOption={watch('state')}
        options={CONTENT_RADIO_BUTTON_OPTIONS}
        title="Create Portal Content"
        isFormValid={isUpdateButtonEnabled}
        mainButtonText={BUTTON_TEXT}
      />
      <div className="mt-7 w-[456px] mx-auto">
        <RichText
          title="Content Creation"
          description="Create content for your pages dynamically per language"
          className="mb-9"
        />
        <Form {...contentFormConfig}>
          <form>
            <div className="flex flex-col gap-5 mb-9">
              {enabledLanguages.length > 1 && (
                <div className="grid grid-cols-2 gap-5">
                  <FormField
                    name="localization"
                    control={control}
                    render={({ field: { value, onChange } }) => {
                      return (
                        <FormItem>
                          <LanguagePicker
                            languages={enabledLanguages}
                            defaultSelectedLanguage={value || DEFAULT_LANGUAGE}
                            onChange={(newValue) => {
                              handleLanguageChange(newValue, onChange);
                            }}
                            languageStatus={languageStatus}
                            dataTestId="ContentForm-LanguagePicker"
                            listDataTestId="ContentForm-LanguagePicker-List"
                          />
                        </FormItem>
                      );
                    }}
                  />
                </div>
              )}
              <FormField
                name="title"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        label="Title"
                        placeholder="e.g., Blog"
                        isRequired={true}
                        state={errors.title?.message ? 'error' : 'default'}
                        description={errors.title?.message}
                        value={value}
                        onChange={onChange}
                        data-testid="ContentForm-Input-Title"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-2 gap-5">
                <FormField
                  name="subTitle"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          label="Description"
                          placeholder="Add description"
                          state={errors.subTitle?.message ? 'error' : 'default'}
                          description={errors.subTitle?.message}
                          value={value}
                          onChange={onChange}
                          data-testid="ContentForm-Input-Description"
                          isRequired={true}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  name="labels"
                  control={control}
                  render={({ field: { onChange } }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          label="Labels"
                          placeholder="Add some label"
                          value={labelsDisplayValue}
                          data-testid="ContentForm-Input-Labels"
                          onChange={(e) =>
                            onChange(
                              e.target.value
                                .split(',')
                                .map((v) => v.trim())
                                .filter((v) => v.length > 0)
                            )
                          }
                          onFocus={() => markAsInteracted('labels')}
                          isRequired={true}
                          description={
                            interactedFields.has('labels')
                              ? errors.labels?.message
                              : undefined
                          }
                          state={
                            interactedFields.has('labels') &&
                            errors.labels?.message
                              ? 'error'
                              : 'default'
                          }
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                name="bodyText"
                control={control}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div>
                        <MarkdownEditorWrapper
                          label="Body text"
                          allowSwitching
                          placeholder="e.g., Write your blog post here. Share exciting updates, game features, behind-the-scenes insights, or special offers."
                          isRequired={true}
                          value={field.value}
                          onChange={(value) => {
                            if (value !== initialValues.bodyText) {
                              markAsInteracted('bodyText');
                            }
                            field.onChange(value);
                          }}
                        />
                      </div>
                    </FormControl>
                    {interactedFields.has('bodyText') &&
                      errors.bodyText?.message && (
                        <FormMessage className="mt-1 text-p-s text-base-destructive" />
                      )}
                  </FormItem>
                )}
              />
              <FormField
                name="headerImage"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <FormItem>
                    <FormControl>
                      <UploadImage
                        className="w-full"
                        title="Add header image"
                        description="The image will be scaled to 32px x 32px"
                        buttonText="Upload"
                        isRequired
                        selectedImageUrl={value}
                        onUpload={(url: string) => {
                          onChange(url);
                        }}
                        data-testid="ContentForm-HeaderUpload"
                        buttonDataTestId="ContentForm-HeaderUpload-Btn"
                      />
                    </FormControl>
                    <FormMessage className="mt-1 text-p-s text-base-destructive" />
                  </FormItem>
                )}
              />
              <FormField
                name="thumbnailImage"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <FormItem>
                    <UploadImage
                      className="w-full"
                      title="Add thumbnail image"
                      description="The image will be scaled to 32px x 32px"
                      buttonText="Upload"
                      isRequired
                      selectedImageUrl={value}
                      onUpload={(url: string) => {
                        onChange(url);
                      }}
                      data-testid="ContentForm-ThumbnailUpload"
                      buttonDataTestId="ContentForm-ThumbnailUpload-Btn"
                    />
                    <FormMessage className="mt-1 text-p-s text-base-destructive" />
                  </FormItem>
                )}
              />
              <Separator className="w-[456px] mx-auto mt-5 mb-2 bg-base-sidebarBorder" />
              <RichText
                title="Content Settings"
                description="Set your content permanently for all languages"
                className="mb-5"
              />
              <div className="grid grid-cols-2 gap-5">
                <FormField
                  name="pages"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <FormItem>
                      <PagesSelect
                        label="Pages"
                        onOptionChange={onChange}
                        value={value ?? []}
                        disabled={!isDefaultLanguage(localizationValue?.value)}
                        buttonDataTestId="ContentForm-Select-Pages"
                        listDataTestId="ContentForm-Select-Pages-List"
                        optionDataTestIdPrefix="ContentForm-Select-Pages-Option"
                      />
                    </FormItem>
                  )}
                />
                <FormField
                  name="contentType"
                  control={control}
                  render={({ field: { onChange } }) => (
                    <FormItem>
                      <FormControl>
                        <InputSelectDropdown
                          label="Content type"
                          defaultSelected={CONTENT_TYPE_OPTIONS.find(
                            (option) => option.value === watch('contentType')
                          )}
                          options={CONTENT_TYPE_OPTIONS}
                          onOptionChange={onChange}
                          disabled={
                            !isDefaultLanguage(localizationValue?.value)
                          }
                          buttonDataTestId="ContentForm-Select-ContentType"
                          listDataTestId="ContentForm-Select-ContentType-List"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-5">
                <FormField
                  name="slug"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <FormItem>
                      <Input
                        label="Slug"
                        placeholder="/"
                        value={value}
                        onChange={onChange}
                        data-testid="ContentForm-Input-Slug"
                        onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                          setValue('slug', sanitizeSlug(e.target.value), {
                            shouldDirty: true,
                            shouldValidate: true
                          });
                        }}
                        state={errors.slug?.message ? 'error' : 'default'}
                        description={errors.slug?.message}
                        disabled={!isDefaultLanguage(localizationValue?.value)}
                      />
                    </FormItem>
                  )}
                />
                <FormField
                  name="visibility"
                  control={control}
                  render={({ field: { onChange } }) => (
                    <FormItem>
                      <FormControl>
                        <InputSelectDropdown
                          label="Visibility"
                          defaultSelected={VISIBILITY_OPTIONS.find(
                            (option) => option.value === watch('visibility')
                          )}
                          options={VISIBILITY_OPTIONS}
                          onOptionChange={onChange}
                          disabled={
                            !isDefaultLanguage(localizationValue?.value)
                          }
                          dataTestId="Visibility-Label"
                          buttonDataTestId="Visibility-Dropdown"
                          listDataTestId="Visibility-List"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-5">
                <FormField
                  name="tag"
                  control={control}
                  render={({
                    field: { value, onChange },
                    fieldState: { error }
                  }) => (
                    <FormItem>
                      <FormControl>
                        <TagSelector
                          value={value}
                          onChange={onChange}
                          error={error?.message}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              {watch('state') === EContentRadioButtonOptions.HIDDEN && (
                <div className="w-full mt-9">
                  <RichText
                    title="Schedule publication date"
                    description="Set a date for your content to go live"
                    className="mb-5"
                  />
                  <FormField
                    control={control}
                    name="isPublishDateEnabled"
                    render={({ field }) => (
                      <Switch
                        key={field.value ? 'on' : 'off'}
                        className="mb-9"
                        label="Enable scheduled publishing"
                        description="Turn on scheduled publishing for this content"
                        textAlignment="right"
                        defaultChecked={field.value}
                        checked={field.value}
                        onCheckedChange={(checked) => {
                          field.onChange(checked);
                          if (!checked) {
                            setValue('publishDate', '');
                          }
                        }}
                      />
                    )}
                  />
                  <FormField
                    name="publishDate"
                    control={control}
                    render={({ field }) => {
                      return (
                        <FormItem>
                          <DatePicker
                            shouldExcludePast
                            displayInUTC
                            label="Publish date"
                            infoTooltipContent="All times shown in UTC"
                            disabled={!watch('isPublishDateEnabled')}
                            {...field}
                          />
                        </FormItem>
                      );
                    }}
                  />
                </div>
              )}
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};

export default ContentForm;
